import merge from 'lodash.merge'
import type { CountryConfig } from './models/types/countryConfig.js'
import type { LabelledItem } from './models/types/labelledItems.js'
import type { NormalizedRegionConfig } from './models/types/regionConfig.js'
import type { PhoneConfig } from './models/types/phoneConfig.js'
import regionConfigJson from './compiled/regionConfig.json'
import restrictedCountriesJson from './compiled/restrictions.json'
import sanctionedCountriesJson from './compiled/sanctions.json'
import currencyCodes from './compiled/currency.json'
import countriesJson from './compiled/countries.json'
import languagesJson from './compiled/languages.json'

const LOCALE_SEPARATOR_REGEXP = /[-_]/
const LOCALE_OUTPUT_SEPARATOR = '-'

const languages: Record<string, Record<string, string>> = languagesJson
const countries: Record<string, Record<string, string>> = countriesJson
const restrictedCountries: Record<string, string> = restrictedCountriesJson
const sanctionedCountries: Record<string, string> = sanctionedCountriesJson
const regionConfig: Record<string, NormalizedRegionConfig> = JSON.parse(JSON.stringify(regionConfigJson))

const rawCountryConfigMemo: Record<string, NormalizedRegionConfig> = {}

let phonesConfigMemo: Record<string, PhoneConfig> = {}
let features: Record<string, string[]> | null = null // caching to avoid repeated logic
let shouldEnableBetaCountries = false

/**
 * Get raw country config merged with default options
 *
 * i.e. merge default config with country-specific overrides
 *
 * @private
 * @param locale - locale, country code, or 'DEFAULT'
 */
function _getRawCountryConfig (locale?: string): NormalizedRegionConfig | Record<string, never> {
  const { countryCode } = getLocaleParts(locale)
  if (!countryCode) return {}

  let rawCountryConfig: NormalizedRegionConfig

  if (rawCountryConfigMemo.hasOwnProperty(countryCode)) {
    rawCountryConfig = rawCountryConfigMemo[countryCode]
  } else {
    rawCountryConfig = merge({}, regionConfig.DEFAULT, regionConfig[countryCode as keyof typeof regionConfig])
    rawCountryConfigMemo[countryCode] = rawCountryConfig
  }

  return rawCountryConfig
}

/**
 * Normalizes country config by mapping it to the way we want to consume it in our apps
 *
 * @private
 * @param locale - locale or country code
 */
function _getNormalizedCountryConfig (locale: string): CountryConfig {
  const { countryCode } = getLocaleParts(locale)
  const rawCountryConfig = _getRawCountryConfig(countryCode)

  let { betaLanguages, supportedLanguages } = rawCountryConfig

  if (!shouldEnableBetaCountries) {
    supportedLanguages = supportedLanguages?.filter(language => !betaLanguages?.includes(language))
  }

  return {
    defaultLanguage: composeLocaleFromParts({ countryCode }, rawCountryConfig.defaultLanguage),
    supportedLanguages: _normalizeLocales(supportedLanguages, countryCode),
    addressFieldsSortOrder: rawCountryConfig.addressFieldsSortOrder,
    promotionalMerchants: rawCountryConfig.promotionalMerchants,
    customerSupportEmail: rawCountryConfig.customerSupportEmail,
    customerSupportPhoneNumber: rawCountryConfig.customerSupportPhoneNumber,
    consumer: rawCountryConfig.rules.consumer,
    address: rawCountryConfig.rules.address,
    billingAddressFormat: rawCountryConfig.genericSettings?.billingAddressFormat
  }
}

/**
 * Takes a list of locales and converts it to a list of two character language codes
 *
 * @private
 */
function _localesToLanguageCodes (locales: string[] = []) {
  return locales.map(locale => locale.substring(0, 2))
}

/**
 * Takes a list of locales and ensures the country code is available
 *
 * @private
 * @param locales - The list of locales to normalize to full locales
 * @param countryCode - The countryCode to append to the locales
 */
function _normalizeLocales (locales: string[] | undefined, countryCode: string | undefined) {
  if (!locales || !countryCode) return []

  const fullLocales = locales.map((locale) => composeLocaleFromParts({ countryCode }, locale))
  const dedupedLocales = new Set(fullLocales)
  return Array.from(dedupedLocales)
}

/**
 * Formats a locale to our standard format and takes in options for overrides
 *
 * @private
 * @param overrides - the parts of the locale we want to override
 * @param overrides.countryCode - overrides the locale's countryCode
 * @param overrides.languageCode - overrides the locale's languageCode (uncommon)
 * @param overrides.separator - overrides the locale's separator, (dash is default)
 * @param locale - the locale to format (or to extract countryCode/languageCode from)
 * @returns formatted locale
 */
export function composeLocaleFromParts (overrides: {
  countryCode?: string
  languageCode?: string
  separator?: string
}, locale?: string) {
  let { countryCode, languageCode, separator } = { ...getLocaleParts(locale), ...overrides }
  separator = separator || LOCALE_OUTPUT_SEPARATOR

  const lowerLanguageCode = languageCode?.substring(0, 2).toLowerCase()
  const upperCountryCode = countryCode?.toUpperCase()
  return [lowerLanguageCode, upperCountryCode].filter(Boolean).join(separator)
}

/**
 * Takes a locale and gives us back the country and language parts if they are available
 *
 * @param locale - full or partial locale
 */
export function getLocaleParts (locale?: string) {
  let languageCode: string | undefined, countryCode: string | undefined

  if (locale) {
    if (locale.length > 2) {
      // we have both locale parts
      [languageCode, countryCode] = locale.split(LOCALE_SEPARATOR_REGEXP)
    } else if (locale.toLowerCase() === locale) {
      // it's all lower case, assume it's the language code
      languageCode = locale
    } else if (locale.toUpperCase() === locale) {
      // it's all upper case, assume it's the country code
      countryCode = locale
    }
  }

  if (countryCode) {
    countryCode = countryCode.toUpperCase()
  }

  return { languageCode, countryCode }
}

/**
 * Sorts an array of objects by a particular key
 *
 * @private
 * @param collection - the collection of objects to sort
 * @param key - the key to sort by
 */
function _sortByKey <T> (collection: T[], key: keyof T) {
  return collection.sort((a, b) => {
    if (a[key] > b[key]) return 1
    if (b[key] > a[key]) return -1
    return 0
  })
}

/**
 * Sets the shouldEnableBetaCountries flag
 *
 * @param value - the Boolean value to set shouldEnableBetaCountries to
 */
export function enableBetaCountries (value: boolean) {
  shouldEnableBetaCountries = value
}

/**
 * Returns a country list with the features available per country, in the format:
 * ```
 * {
 *   US: ['featureA', 'featureB'],
 *   GB: ['featureB']
 * }
 * ```
 */
export function getCountryFeatures () {
  if (features) return features

  features = {}
  for (const countryCode in regionConfig) {
    const rawCountryConfig = _getRawCountryConfig(countryCode)

    if (Array.isArray(rawCountryConfig.features)) {
      features[countryCode] = rawCountryConfig.features
    }
  }

  return features
}

/**
 * Gives you the list of countries where the app is live
 */
export function getLiveCountries () {
  const allCountries = Object.keys(regionConfig).sort()
  return allCountries.filter((countryCode) => {
    return isCountryLive(countryCode)
  })
}

/**
 * Given a country, determines if we are live in that country
 *
 * @param locale - The locale or country code to check if its supported
 */
export function isCountryLive (locale?: string) {
  const { beta, showInLocaleSelector } = _getRawCountryConfig(locale)
  return shouldEnableBetaCountries ? !!showInLocaleSelector : !!(showInLocaleSelector && !beta)
}

/**
 * Given a locale, determines if the country code is restricted
 * the address form is hidden for the restricted countries
 *
 * @example
 * isCountryRestricted('UA')
 * // => true
 * isCountryRestricted('KP')
 * // => false
 * isCountryRestricted('US')
 * // => false
 *
 * @param locale - locale or country code to check if restricted
 */
export function isCountryRestricted (locale?: string) {
  const { countryCode } = getLocaleParts(locale)
  return !!countryCode && restrictedCountries.hasOwnProperty(countryCode)
}

/**
 * Given a locale, determines if the country code is sanctioned
 *
 * @example
 * isCountrySanctioned('ko-KP')
 * // => true
 * isCountrySanctioned('KP')
 * // => true
 * isCountrySanctioned('US')
 * // => false
 *
 * @param locale - locale or country code to check if sanctioned
 */
export function isCountrySanctioned (locale?: string) {
  const { countryCode } = getLocaleParts(locale)
  return !!countryCode && sanctionedCountries.hasOwnProperty(countryCode)
}

/**
 * Given a locale or country code, determines if the country is supported
 *
 * @example
 * isCountrySupported('fr-US')
 * // => true
 * isCountrySupported('fr-YY')
 * // => false
 * isCountrySupported('US')
 * // => true
 * isCountrySupported('YY')
 * // => false
 *
 * @param locale - locale or country code to check if it's supported
 */
export function isCountrySupported (locale?: string) {
  const { countryCode } = getLocaleParts(locale)
  return !!countryCode && countries.en.hasOwnProperty(countryCode)
}

/**
 * Given a feature name and locale, determines if requested country feature should be enabled
 *
 * @param feature - feature name
 * @param locale - locale or country code
 */
export function isCountryFeatureEnabled (feature: string, locale: string) {
  const features = getCountryFeatures()
  const { countryCode } = getLocaleParts(locale)
  const countryFeatures = countryCode && features[countryCode]

  return !!countryFeatures && countryFeatures.includes(feature)
}
/**
 * Given a full locale, determines if it's a supported locale
 *
 * @param locale - The full locale formatted string to check
 * @param liveCountriesOnly - whether to ignore countries that are not live
 */
export function isLocaleSupported (locale?: string, liveCountriesOnly = true) {
  const { languageCode, countryCode } = getLocaleParts(locale)

  if (liveCountriesOnly && !isCountryLive(countryCode)) {
    return false
  }

  const { supportedLanguages } = _getRawCountryConfig(countryCode)
  return !!languageCode && _localesToLanguageCodes(supportedLanguages).includes(languageCode)
}

/**
 * Given a LIVE country, returns an object with that country's language and
 * address sort order config in the format:
 * ```
 * { defaultLanguage, supportedLanguages, addressFieldsSortOrder, promotionalMerchants }
 * ```
 *
 * @param locale - The locale or country code to get configs for
 * @param liveCountriesOnly - whether to ignore countries that are not live
 */
export function getCountryConfig (locale: string, liveCountriesOnly = true) {
  const { countryCode } = getLocaleParts(locale)
  const shouldIgnoreCountry = liveCountriesOnly && !isCountryLive(countryCode)

  if (!countryCode || shouldIgnoreCountry) return

  const normalizedCountryConfig = _getNormalizedCountryConfig(countryCode)
  return normalizedCountryConfig
}

/**
 * Determine the language code given a locale
 * This is to accommodate the case when a locale can be generated in the build file if that locale
 * requires a specific language script.
 *
 * @private
 * @param locale - locale or language code
 */
function _getLanguageCode (locale?: string) {
  return locale && languages.hasOwnProperty(locale) ? locale : getLocaleParts(locale).languageCode
}

/**
 * Get a list of language names in native spelling and a given language, formatted
 *
 * @param locale - The locale or language code you wish to translate to
 * @param languageSubset - A languageSubset of languages to get translated, if none is passed the whole list is returned
 */
export function getTranslatedLanguages (locale?: string, languageSubset?: string[]) {
  const languageCode = _getLanguageCode(locale)
  if (!languageCode || !languages.hasOwnProperty(languageCode)) {
    return []
  }

  if (!Array.isArray(languageSubset)) {
    languageSubset = Object.keys(languages[languageCode])
  }

  const output: LabelledItem[] = []

  for (const language of languageSubset) {
    const index = language.substring(0, 2)
    if (!languages[languageCode][index]) continue

    output.push({
      value: language,
      label: languages[languageCode][index],
      // only add subtext if it differs from label
      ...(languageCode !== index && { subtext: languages[index][index] })
    })
  }

  return _sortByKey(output, 'label')
}

/**
 * Get a language's name in the given locale
 *
 * @param locale - locale containing the language code to derive the translated name from
 * @param translationLocale - locale containing the language code to translate into if available
 */
export function getLanguageName (locale?: string, translationLocale = 'en') {
  const { languageCode } = getLocaleParts(locale)
  const translationLanguageCode = _getLanguageCode(translationLocale)

  let languageName: string | undefined

  if (translationLanguageCode && languages.hasOwnProperty(translationLanguageCode) && languageCode) {
    languageName = languages[translationLanguageCode][languageCode]
  }

  return languageName
}

/**
 * Get a country's name in the given locale
 * Sanctioned country names are not translated and default to English
 *
 * @param locale - locale containing the language code to derive the translated name from
 * @param translationLocale - locale containing the language code to translate into if available
 */
export function getCountryName (locale = 'en', translationLocale = 'en') {
  const { countryCode } = getLocaleParts(locale)
  if (!countryCode) return

  const languageCode = _getLanguageCode(translationLocale)

  let countryName: string | undefined

  if (isCountrySanctioned(countryCode)) {
    countryName = sanctionedCountries[countryCode]
  } else if (languageCode && countries.hasOwnProperty(languageCode)) {
    countryName = countries[languageCode][countryCode]
  }

  return countryName
}

/**
 * Gets the list of countries translated to the given locale
 *
 * @param locale - the locale or languageCode to translate the country list to
 * @param subset - A subset of countryCodes you want to get the names for, if no subset given, the whole list of countries will be returned.
 */
export function getTranslatedCountries (locale?: string, subset: string[] = []) {
  const languageCode = _getLanguageCode(locale)
  if (!languageCode || !countries.hasOwnProperty(languageCode)) {
    return []
  }

  if (Array.isArray(subset)) {
    subset = subset.filter(countryCode => countries[languageCode][countryCode])
  }

  if (!Array.isArray(subset) || subset.length === 0) {
    subset = Object.keys(countries[languageCode])
  }

  const output: LabelledItem[] = subset.map((countryCode) => ({
    value: countryCode,
    label: countries[languageCode][countryCode]
  }))

  return _sortByKey(output, 'label')
}

/**
 * Returns a fallback decision map to be used in the i18n package based on this documenation
 * https://kazupon.github.io/vue-i18n/guide/fallback.html#explicit-fallback-with-decision-maps
 *
 * Basically forms a locale combination of the languageCode component from each of the supportedLanguages
 * and pairs it up with the key of the country configuration as the countryCode component.
 *
 * i.e. if the countryConfig key we are looking at is:
 * ```
 * "ZZ": {
 *   supportedLanguages: ["en-AA", "es-ZZ", "fr-ZZ"]
 * }
 * ```
 *
 * We'll make a locale with each of the "en", "es", and "fr" languageCodes and add the countryCode "-ZZ" since that's the key for the config.
 * So then we de-dupe all of the locales we form this way from the list of supported languages (i.e. "en-ZZ", "es-ZZ", and "fr-ZZ").
 * Since "en-AA" is the only one that doesn't match one of those, that will be the only locale returned in the fallback config for country ZZ.
 * We then take that locale's language 'en', pair it with the country config's key 'ZZ' and then set that equal to the fallback 'en-AA'.
 *
 * Which results in: `{ "en-ZZ": ["en-AA"] }` if no default fallback language is specified,
 * or `{ "default": ["en"], "en-ZZ": ["en-AA"] }` if `en` is passed as default fallback language.
 *
 * @param defaultFallback - The default fallback for the application to use after a particular locale has exhausted all of its fallback options.
 * @returns fallback decision map: `{ "default": ["en"], "en-AU": ["en-GB"], "en-MA": ["en-TH"] }`
 */
export function getFallbackConfig (defaultFallback?: string) {
  const output: Record<string, string[]> = defaultFallback ? { default: [defaultFallback] } : {}

  for (const countryCode in regionConfig) {
    const { supportedLanguages } = _getRawCountryConfig(countryCode)
    if (!supportedLanguages) continue

    supportedLanguages.forEach((language) => {
      if (language.length > 2) {
        const [languageCode] = language.split(LOCALE_SEPARATOR_REGEXP)
        const key = composeLocaleFromParts({ countryCode, languageCode })

        // in the event that someone puts the full locale for a country when not needed
        if (key !== language) {
          output[key] = [language]
        }
      }
    })
  }

  return output
}

/**
 * Formats a locale to our standard format, optionally it takes a separator if and a transforming method
 *
 * @param locale - The locale to format
 * @param separator - The separator to use, defaults to dash
 * @param transformer - A transforming method to format the output. example: String.prototype.toLowerCase
 */
export function formatLocale (locale?: string, separator?: string, transformer?: (s: string) => string) {
  if (!locale || locale.length < 5) return null
  let output = composeLocaleFromParts({ separator }, locale)

  if (typeof transformer === 'function') {
    output = transformer.call(output, output)
  }

  return output
}

/**
 * Get phone config for all countries
 *
 * This returns the data in a format compatible for use with Al-Phone-Input
 *
 * @returns key-value pair of two-letter country ISOs to their respective phone configs
 */
export function getPhonesConfig (): Record<string, PhoneConfig> {
  if (Object.keys(phonesConfigMemo).length > 0) {
    return phonesConfigMemo
  }

  const phonesConfig: Record<string, PhoneConfig> = {}

  for (const countryCode in regionConfig) {
    if (countryCode === 'DEFAULT' || isCountrySanctioned(countryCode)) continue
    phonesConfig[countryCode] = getCountryPhoneConfig(countryCode)
  }

  phonesConfigMemo = phonesConfig

  return phonesConfig
}

/**
 * Get phone config for a particular country
 *
 * @param locale - locale or country code
 */
export function getCountryPhoneConfig (locale: string) {
  const { rules } = _getRawCountryConfig(locale)
  const { phoneCode, phoneNumber } = rules.consumer
  const countryPhoneConfig: PhoneConfig = {}

  if (phoneCode) {
    countryPhoneConfig.code = (phoneCode as unknown) as string
  }

  if (phoneNumber) {
    const { mask, minimumLength: min, noLeadingNumber } = phoneNumber
    countryPhoneConfig.mask = mask
    countryPhoneConfig.min = min

    if (noLeadingNumber) {
      countryPhoneConfig.noLeadingNumber = noLeadingNumber
    }
  }

  return countryPhoneConfig
}

/**
 * Get the country code that a phone number belongs to based on a phone number's calling code
 *
 * If there's more than one match in our config, make some assumptions by factoring in the consumer's country code
 * If there's only one match, return it
 * Else, default to the consumer's country code as our best guess
 *
 * @param callingCode - numerical phone code prefix for international calling (i.e. "1" for many countries including USA, "81" for Japan)
 * @param locale - consumer's locale or country code
 * @returns if discovered, the ISO country code that the phone number (probably) belongs to
 */
export function getPhoneCountryISO (callingCode?: string, locale?: string) {
  const { countryCode: consumerCountryCode } = getLocaleParts(locale)
  const countryOptions = getPhonesConfig()

  if (callingCode === '1') {
    const shouldAssumeCountryCodeCorrelation = consumerCountryCode && countryOptions[consumerCountryCode].code === '1'
    return shouldAssumeCountryCodeCorrelation ? consumerCountryCode : 'US'
  }

  for (const ISO in countryOptions) {
    if (countryOptions[ISO].code === callingCode) {
      return ISO
    }
  }

  return consumerCountryCode
}

/**
 * Returns the phone's mask based on the countryCode.
 *
 * A fallback consumer locale should be provided to improve accuracy for when there are multiple country matches found for a callingCode.
 *
 * @param callingCode - numerical phone code prefix for international calling (i.e. "1" for many countries including USA, "81" for Japan)
 * @param locale - consumer's locale or country code
 * @returns if discovered, the formatted masked phone number, or the formatted unmasked number if the associated country ISO does not mask the number
 */
export function getPhoneMaskingRule (callingCode: string | number, locale?: string) {
  if (typeof callingCode === 'number') {
    callingCode = String(callingCode)
  }

  const { countryCode: consumerCountryCode } = getLocaleParts(locale)
  const phoneCountryISO = getPhoneCountryISO(callingCode, consumerCountryCode)
  const countryOptions = getPhonesConfig()

  if (phoneCountryISO && countryOptions[phoneCountryISO]) {
    return countryOptions[phoneCountryISO].mask || null
  }

  return null
}

/**
 * Returns the user-friendly localized transaction amount.
 *
 * @param transactionAmount EMVCo-defined transaction amount
 */
export function getLocalizedAmount ({ transactionAmount, transactionCurrencyCode }: {
  transactionAmount: string | number
  transactionCurrencyCode: string
} = {
  transactionAmount: '',
  transactionCurrencyCode: ''
}, fallbackLocale: string) {
  const amount = typeof transactionAmount === 'string'
    ? parseFloat(transactionAmount)
    : transactionAmount

  if (amount) {
    try {
      // find the three-letter ISO-4217 currency code from the lookup table
      // if not present, assume that it is the three-letter currency code itself
      const currencyKey = transactionCurrencyCode as keyof typeof currencyCodes
      const isoSafeCurrencyCode = currencyCodes[currencyKey] || transactionCurrencyCode

      return amount.toLocaleString(fallbackLocale, {
        style: 'currency',
        currency: isoSafeCurrencyCode
      })
    } catch (_) {
      // if a three-letter ISO-4217 could not be derived, fall back to a two-decimal fixed number
      return amount.toFixed(2)
    }
  }

  return ''
}
